import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// Import react-pdf styles for text and annotation layers
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Configure PDF.js worker - use unpkg CDN with HTTPS
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function PDFViewer({ document, page = 1, onBack, initialScale = 1.0 }) {
  const [currentPage, setCurrentPage] = useState(page);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(initialScale);
  const [loading, setLoading] = useState(true);

  // Get PDF through kb-backend API with credentials
  const pdfUrl = document ? {
    url: `/api/documents/by-name/${encodeURIComponent(document.name)}`,
    withCredentials: true
  } : null;

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    setLoading(false);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (numPages && currentPage < numPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleZoomIn = () => {
    setScale(Math.min(scale + 0.25, 2.0));
  };

  const handleZoomOut = () => {
    setScale(Math.max(scale - 0.25, 0.5));
  };

  return (
    <div className="flex flex-col h-full bg-galaxy-950">
      {document ? (
        <>
          {/* Toolbar */}
          <div className="bg-galaxy-900/80 border-b border-galaxy-800/50 px-4 py-3">
            <div className="flex items-center gap-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 text-galaxy-400 hover:text-white hover:bg-galaxy-800/50 rounded-lg transition-colors"
                  title="Back to documents"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </button>
              )}
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-white truncate">{document.title}</h2>
                <p className="text-xs text-galaxy-400">
                  {document.author || document.category} • Page {currentPage} {numPages ? `of ${numPages}` : ''}
                </p>
              </div>

              {/* Page Navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="p-1.5 text-galaxy-400 hover:text-white hover:bg-galaxy-800/50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <span className="text-xs text-galaxy-400 min-w-[60px] text-center">
                  {currentPage} / {numPages || '?'}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={!numPages || currentPage === numPages}
                  className="p-1.5 text-galaxy-400 hover:text-white hover:bg-galaxy-800/50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>

              {/* Zoom Controls */}
              <div className="flex items-center gap-2 border-l border-galaxy-700/50 pl-3">
                <button
                  onClick={handleZoomOut}
                  className="px-2 py-1 text-xs text-galaxy-400 hover:text-white hover:bg-galaxy-800/50 rounded"
                >
                  -
                </button>
                <span className="text-xs text-galaxy-400 min-w-[45px] text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="px-2 py-1 text-xs text-galaxy-400 hover:text-white hover:bg-galaxy-800/50 rounded"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* PDF Content */}
          <div className="flex-1 overflow-auto bg-galaxy-900/30 p-4">
            <div className="flex justify-center">
              {loading && (
                <div className="text-white text-center py-8">
                  <div className="text-lg mb-2">Loading PDF...</div>
                </div>
              )}
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="text-white text-center py-8">
                    <div className="text-lg mb-2">Loading PDF...</div>
                  </div>
                }
                error={
                  <div className="text-red-400 text-center py-8">
                    <div className="text-lg mb-2">Failed to load PDF</div>
                    <div className="text-sm">Please check if the document exists</div>
                  </div>
                }
              >
                <Page
                  pageNumber={currentPage}
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  className="shadow-2xl"
                />
              </Document>
            </div>
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h3 className="text-lg font-medium text-galaxy-400 mb-2">No Document Selected</h3>
            <p className="text-sm text-galaxy-500">Select a document to view</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default PDFViewer;
